import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Clock, MapPin, ChevronRight, Flame, ArrowLeft, Store } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public — no auth
const publicApi = axios.create({ baseURL: API });

export default function TodaysDealsPage() {
  const { branchCode } = useParams(); // optional
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    setLoading(true);
    const load = async () => {
      try {
        if (branchCode) {
          const r = await publicApi.get(`/public/deals`, { params: { branch_code: branchCode } });
          setData(r.data);
        } else {
          const [r, b] = await Promise.all([
            publicApi.get(`/public/deals`),
            publicApi.get(`/public/deals/branches`),
          ]);
          setData(r.data);
          setBranches(b.data);
        }
      } catch (e) {
        // Show empty state
        setData(branchCode ? { deals: [] } : { branches: [] });
      } finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line
  }, [branchCode]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8EE] via-white to-[#FEEBC8]" data-testid="todays-deals-page">
      {/* Header */}
      <header className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        {branchCode && (
          <Link to="/deals" className="inline-flex items-center gap-1 text-xs text-[#5C4033] hover:text-[#C05621] mb-4">
            <ArrowLeft className="w-3 h-3" /> All branches
          </Link>
        )}
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#C05621] to-[#8B4513] text-white flex items-center justify-center shadow-lg">
            <Flame className="w-7 h-7" />
          </div>
          <div>
            <div className="uc-label text-[#C05621]">Bakemill · Fresh Deals</div>
            <h1 className="font-[Outfit] text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#2C1810]">Today's Deals</h1>
            <p className="text-sm text-[#5C4033] mt-1">Fresh-baked goodies at up to 50% off — first come, first served.</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-16 text-[#8B7355]">Loading fresh deals…</div>
        ) : branchCode ? (
          <BranchDealsView data={data} />
        ) : (
          <AllBranchesView data={data} allBranches={branches} />
        )}
      </main>

      <footer className="border-t border-[#EAE5D9] bg-white py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-xs text-[#8B7355]">
          Deals refresh in real time based on freshness and stock. Available at store — no online checkout yet.
          <div className="mt-1">© Bakemill Foods · Baked fresh, sold honest.</div>
        </div>
      </footer>
    </div>
  );
}

function BranchDealsView({ data }) {
  const deals = data?.deals || [];
  const branch = data?.branch;
  if (deals.length === 0) {
    return (
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-10 text-center max-w-xl mx-auto">
        <Sparkles className="w-10 h-10 mx-auto text-[#8B7355] opacity-50 mb-3" />
        <h2 className="font-[Outfit] text-xl font-semibold">No deals available right now</h2>
        <p className="text-sm text-[#5C4033] mt-1">Check back a little later — our next fresh batches will drop soon.</p>
      </div>
    );
  }
  return (
    <>
      {branch && (
        <div className="flex items-center gap-2 mb-6 text-sm">
          <Store className="w-4 h-4 text-[#C05621]" />
          <span className="font-medium text-[#2C1810]">{branch.name}</span>
          {branch.city && <><span className="text-[#8B7355]">·</span><span className="text-[#8B7355] flex items-center gap-1"><MapPin className="w-3 h-3" /> {branch.city}</span></>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deals-grid">
        {deals.map((d) => <DealCard key={d.batch_id} d={d} />)}
      </div>
    </>
  );
}

function AllBranchesView({ data, allBranches }) {
  const rows = data?.branches || [];
  if (rows.length === 0) {
    // No branches have live deals — still show branch directory
    return (
      <div className="space-y-6">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-8 text-center max-w-xl mx-auto">
          <Sparkles className="w-10 h-10 mx-auto text-[#8B7355] opacity-50 mb-3" />
          <h2 className="font-[Outfit] text-xl font-semibold">No live deals at the moment</h2>
          <p className="text-sm text-[#5C4033] mt-1">Our team is still baking today's fresh batch. Come back a little later.</p>
        </div>
        {allBranches.length > 0 && (
          <div>
            <div className="uc-label text-[#5C4033] mb-3 text-center">Visit one of our branches</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl mx-auto">
              {allBranches.map((b) => (
                <Link key={b.id} to={`/deals/${b.code}`} className="block bg-white border border-[#EAE5D9] rounded-lg p-3 hover:border-[#C05621] hover:shadow-md transition">
                  <div className="font-medium text-sm">{b.name}</div>
                  <div className="text-[10px] text-[#8B7355] mt-0.5">{b.code}{b.city ? ` · ${b.city}` : ""}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {rows.map(({ branch, deal_count, top_discount, deals }) => (
        <section key={branch.id} className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden shadow-sm" data-testid={`branch-deals-${branch.code}`}>
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#FEF9EA] to-white border-b border-[#EAE5D9]">
            <div className="flex items-center gap-3">
              <Store className="w-5 h-5 text-[#C05621]" />
              <div>
                <div className="font-[Outfit] text-lg font-semibold">{branch.name}</div>
                <div className="text-xs text-[#8B7355]">{branch.city ? branch.city + " · " : ""}{deal_count} deal{deal_count !== 1 ? "s" : ""} · up to {Math.round(top_discount)}% off</div>
              </div>
            </div>
            <Link to={`/deals/${branch.code}`} className="text-[#C05621] hover:text-[#9C4221] text-sm flex items-center gap-1 font-medium">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(deals || []).slice(0, 4).map((d) => <DealCard key={d.batch_id} d={d} compact />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function DealCard({ d, compact }) {
  const urgent = d.hours_left <= 24;
  const tone = urgent ? "from-red-500 to-orange-600" : d.hours_left <= 48 ? "from-amber-500 to-orange-500" : "from-emerald-500 to-teal-500";
  return (
    <div className={`relative rounded-lg overflow-hidden border border-[#EAE5D9] bg-white ${compact ? "" : "shadow-sm hover:shadow-md transition"}`} data-testid={`deal-card-${d.batch_id}`}>
      <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-gradient-to-r ${tone} text-white text-[10px] font-semibold shadow-sm`}>
        {Math.round(d.discount_pct)}% OFF
      </div>
      {d.image_url ? (
        <img src={d.image_url} alt={d.product_name} className={`w-full ${compact ? "h-24" : "h-40"} object-cover`} />
      ) : (
        <div className={`w-full ${compact ? "h-24" : "h-40"} bg-gradient-to-br from-[#FEF9EA] to-[#FEEBC8] flex items-center justify-center text-4xl`}>🍰</div>
      )}
      <div className="p-3">
        <div className={`font-medium ${compact ? "text-sm" : ""} text-[#2C1810] line-clamp-1`}>{d.product_name}</div>
        <div className="text-[10px] text-[#8B7355] mb-2">{d.category || d.product_sku}</div>
        <div className="flex items-end justify-between">
          <div>
            <div className={`font-[Outfit] font-semibold text-[#C05621] ${compact ? "text-lg" : "text-2xl"}`}>₹{d.deal_price}</div>
            <div className="text-[10px] text-[#8B7355] line-through">₹{d.regular_price}</div>
          </div>
          <div className="text-right">
            <div className={`flex items-center gap-1 text-[10px] font-medium ${urgent ? "text-red-600" : "text-amber-700"}`}>
              <Clock className="w-3 h-3" /> {d.hours_left < 1 ? "< 1h" : `${Math.round(d.hours_left)}h left`}
            </div>
            <div className="text-[10px] text-[#8B7355]">Stock: {Math.round(d.stock)}</div>
          </div>
        </div>
        {!compact && (
          <div className="mt-2 text-[10px] px-2 py-1 rounded bg-[#FEF9EA] border border-amber-200 text-amber-800 text-center">
            {d.tier_label}
          </div>
        )}
      </div>
    </div>
  );
}
